import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Play,
  CheckCircle2,
  Sparkles,
  Shield,
  Users,
  Zap,
  X,
  TrendingUp,
  Star,
  MapPin,
  Target,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import AnimatedBackground from "../../components/AnimatedBackground";
import { useSplitTypewriter } from "../../hooks/useTypewriter";
import { useAuth } from "../../context/AuthContext";
import MobileAccordion from "../../components/mobile/MobileAccordion";

const trustIndicators = [
  { icon: Shield, text: "Board-Accurate" },
  { icon: CheckCircle2, text: "Used for Real Exams" },
  { icon: Users, text: "Built for Student & Parents" },
];

// Updated suffix texts
const typewriterSuffixes = [
  "audits your handwritten answer sheets.",
  "diagnoses exactly why marks were lost.",
  "distinguishes silly errors from logic gaps.",
  "turns lost marks into actionable data.",
];

// Board logos
const boardLogos = [
  { name: "CBSE", full: "Central Board of Secondary Education" },
  { name: "ICSE", full: "Indian Certificate of Secondary Education" },
  { name: "JEE", full: "Joint Entrance Examination" },
  { name: "NEET", full: "National Eligibility cum Entrance Test" },
  { name: "State Boards", full: "Various State Education Boards" },
];

// Animated typing cursor
function TypewriterCursor() {
  return (
    <motion.span
      className="inline-block w-1 h-[1em] bg-primary ml-1 align-middle"
      animate={{ opacity: [1, 0, 1] }}
      transition={{ duration: 0.8, repeat: Infinity }}
    />
  );
}

// Side notification popup component
function SideNotification({ notification, onClose }) {
  return (
    <motion.div
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 100, opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="bg-card/95 backdrop-blur-lg border border-border rounded-xl p-4 shadow-xl max-w-xs"
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${notification.iconBg}`}>
          <notification.icon className={`w-5 h-5 ${notification.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium mb-0.5">{notification.title}</p>
          <p className="text-xs text-muted-foreground">
            {notification.message}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

// Notification manager - Shows only 1 notification, dismissable, rare appearance
function NotificationPopups() {
  const [notification, setNotification] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Single notification that shows after 5 seconds, auto-dismisses after 8 seconds
  useEffect(() => {
    if (dismissed) return;
    
    const showTimer = setTimeout(() => {
      setNotification({
        id: 1,
        icon: AlertTriangle,
        iconBg: "bg-amber-500/10",
        iconColor: "text-amber-500",
        title: "Preview Interface",
        message: "The reports and dashboard shown are visual mockups.",
      });
    }, 5000);

    return () => clearTimeout(showTimer);
  }, [dismissed]);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!notification) return;
    
    const dismissTimer = setTimeout(() => {
      setNotification(null);
      setDismissed(true);
    }, 8000);

    return () => clearTimeout(dismissTimer);
  }, [notification]);

  const closeNotification = () => {
    setNotification(null);
    setDismissed(true);
  };

  if (!notification) return null;

  return (
    <div className="fixed right-4 top-24 z-50">
      <AnimatePresence>
        <SideNotification
          key={notification.id}
          notification={notification}
          onClose={closeNotification}
        />
      </AnimatePresence>
    </div>
  );
}

// Glowing badge component
function GlowingBadge({ children, icon: Icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.2 }}
      className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20"
    >
      <motion.div
        className="absolute inset-0 rounded-full bg-primary/20"
        animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0, 0.5] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <Icon className="w-5 h-5 text-primary relative z-10" />
      <span className="text-base font-medium text-primary relative z-10">
        {children}
      </span>
    </motion.div>
  );
}

// Board logo component - illuminated style
function BoardLogo({ name }) {
  return (
    <motion.div
      className="relative group cursor-default"
      whileHover={{ scale: 1.08 }}
    >
      {/* Glow effect */}
      <motion.div className="absolute inset-0 rounded-xl bg-primary/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="relative px-3 py-1.5 md:px-6 md:py-3 rounded-lg md:rounded-xl bg-card/80 border border-primary/20 backdrop-blur-sm shadow-lg shadow-primary/5 group-hover:border-primary/40 transition-all duration-300">
        <span className="text-xs md:text-base font-bold text-foreground/70 group-hover:text-primary transition-colors">
          {name}
        </span>
      </div>
    </motion.div>
  );
}

export default function HeroSection() {
  const { login } = useAuth();
  const { prefix, displaySuffix, isTyping } = useSplitTypewriter(
    "AI that ",
    typewriterSuffixes,
    70,
    3000,
  );

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16 md:pt-20"
    >
      {/* Notification Popups - Hidden on mobile */}
      <div className="hidden md:block">
        <NotificationPopups />
      </div>

      {/* Enhanced Animated Background - Extended to full screen */}
      <div className="hidden md:block absolute inset-0 h-screen">
        <AnimatedBackground variant="full" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-8 md:py-16 lg:py-24 flex-1 flex items-center">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 lg:gap-24 items-center w-full">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center lg:text-left"
          >
            {/* Mobile: Compressed Headline / Desktop: Typewriter with fixed height */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight mb-4 md:mb-8 leading-tight">
              {/* Mobile: Static text */}
              <span className="md:hidden">
                <span className="block text-foreground">AI Exam Analysis</span>
                <span className="block text-primary">
                  Check Why You Lost Marks
                </span>
              </span>

              {/* Desktop: Typewriter effect with fixed min-height to prevent jumping */}
              <span className="hidden md:block" style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <span className="text-foreground">{prefix}</span>
                <span className="font-mono text-primary" style={{ minHeight: '120px', display: 'block' }}>
                  {displaySuffix}
                  {isTyping && <TypewriterCursor />}
                </span>
              </span>
            </h1>

            {/* Mobile: Single line subheadline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-base md:text-xl lg:text-2xl text-muted-foreground mb-6 md:mb-10 max-w-2xl mx-auto lg:mx-0"
            >
              <span className="hidden md:inline">
                Edalyze analyzes exam papers and answer sheets to diagnose
                mistakes, skill gaps, and strategy flaws — with{" "}
                <span className="text-primary font-semibold">
                  board-level accuracy
                </span>
                .
              </span>
              <span className="md:hidden">
                Diagnose mistakes and skill gaps instantly with{" "}
                <span className="text-primary font-semibold">
                  board-level accuracy
                </span>
                .
              </span>
            </motion.p>

            {/* Mobile: ONE CTA only */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start mb-6 md:mb-8"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    data-testid="hero-analyze-btn"
                    className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl shadow-primary/25 h-12 md:h-14 px-6 md:px-10 text-base md:text-lg group"
                  >
                    <Zap className="w-5 h-5 md:w-6 md:h-6 mr-2" />
                    Analyze Paper
                    <ArrowRight className="w-5 h-5 md:w-6 md:h-6 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </motion.div>
              {/* Desktop: Second CTA */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="hidden md:block"
              >
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection("#how-it-works")}
                  data-testid="hero-howit-btn"
                  className="h-14 px-10 text-lg group"
                >
                  <Play className="w-6 h-6 mr-2 group-hover:scale-110 transition-transform" />
                  See How It Works
                </Button>
              </motion.div>
            </motion.div>

            {/* Desktop: Board badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="hidden md:flex items-center justify-center lg:justify-start gap-2 mb-4"
            >
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm text-muted-foreground">
                CBSE • ICSE • JEE • NEET
              </span>
            </motion.div>

            {/* Mobile: "See How It Works" Button - Same as PC */}
            <div className="md:hidden mt-6">
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection("#how-it-works")}
                className="w-full h-12 text-base group"
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                See How It Works
              </Button>
            </div>

            {/* Desktop: Trust Indicators - Hidden on mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="hidden md:flex flex-wrap gap-5 justify-center lg:justify-start mb-6"
            >
              {trustIndicators.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-2 text-base text-muted-foreground"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.15 }}
                  whileHover={{ scale: 1.05, color: "var(--primary)" }}
                >
                  <item.icon className="w-5 h-5 text-primary" />
                  <span>{item.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Desktop: India Badge - Hidden on mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border/50"
            >
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">
                Available only in India for now
              </span>
            </motion.div>
          </motion.div>

          {/* Right - Visual - Static, no floating animations */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="relative hidden lg:flex justify-center items-center"
          >
            {/* Dashboard Preview Card - No hover scale, no floating */}
            <div className="relative">
              {/* Animated Glow Effect */}
              <motion.div
                className="absolute -inset-6 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent rounded-3xl blur-2xl"
                animate={{ opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 4, repeat: Infinity }}
              />

              {/* Main Card - No hover effects, no floating */}
              <div className="relative glass rounded-2xl p-8 border border-primary/20 w-[420px]">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <motion.div
                      className="w-4 h-4 rounded-full bg-red-400"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                    />
                    <motion.div
                      className="w-4 h-4 rounded-full bg-amber-400"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        delay: 0.3,
                      }}
                    />
                    <motion.div
                      className="w-4 h-4 rounded-full bg-green-400"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        delay: 0.6,
                      }}
                    />
                  </div>
                  <motion.span
                    className="text-sm font-mono text-muted-foreground"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    Diagnostic Report
                  </motion.span>
                </div>

                {/* Mock Analysis */}
                <div className="space-y-5">
                  {/* Score Overview */}
                  <div className="flex items-center justify-between p-5 rounded-xl bg-background/50">
                    <div>
                      <p className="text-base text-muted-foreground">
                        Overall Score
                      </p>
                      <motion.p
                        className="text-3xl font-bold"
                        animate={{ scale: [1, 1.03, 1] }}
                        transition={{ duration: 2.5, repeat: Infinity }}
                      >
                        78/100
                      </motion.p>
                    </div>
                    <div className="text-right">
                      <p className="text-base text-muted-foreground">
                        Marks Lost
                      </p>
                      <motion.p
                        className="text-3xl font-bold text-secondary"
                        animate={{ opacity: [1, 0.7, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        -22
                      </motion.p>
                    </div>
                  </div>

                  {/* Error Classification */}
                  <div className="space-y-3">
                    <p className="text-base font-medium">
                      Error Classification
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                          <motion.div
                            className="h-full bg-red-400 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: "45%" }}
                            transition={{
                              delay: 1.2,
                              duration: 1.4,
                              ease: "easeOut",
                            }}
                          />
                        </div>
                        <span className="text-sm w-28">Conceptual (45%)</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                          <motion.div
                            className="h-full bg-amber-400 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: "30%" }}
                            transition={{
                              delay: 1.5,
                              duration: 1.2,
                              ease: "easeOut",
                            }}
                          />
                        </div>
                        <span className="text-sm w-28">Careless (30%)</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                          <motion.div
                            className="h-full bg-primary rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: "25%" }}
                            transition={{
                              delay: 1.8,
                              duration: 1,
                              ease: "easeOut",
                            }}
                          />
                        </div>
                        <span className="text-sm w-28">Strategic (25%)</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Insight */}
                  <motion.div
                    className="p-4 rounded-xl bg-primary/10 border border-primary/20"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2 }}
                  >
                    <motion.p
                      className="text-sm font-mono text-primary"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 2.5 }}
                    >
                      → 8 marks lost due to incomplete derivations in Q3 & Q7
                    </motion.p>
                  </motion.div>
                </div>
              </div>

              {/* Floating Elements - Static, no animations */}
              <div className="absolute -top-4 right-0 glass rounded-xl p-4 border border-primary/20">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  <span className="text-base font-medium">Board Verified</span>
                </div>
              </div>

              <div className="absolute -bottom-4 left-0 glass rounded-xl p-4 border border-primary/20">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-primary" />
                  <span className="text-base font-medium">AI Analysis</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Board Logos Section - Illuminated */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="relative z-10 border-t border-border/50 bg-background/30 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-10">
          <p className="text-center text-xs md:text-base text-muted-foreground mb-4 md:mb-8">
            Trusted for preparation across major Indian boards & exams
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-6 lg:gap-8">
            {boardLogos.map((logo, index) => (
              <BoardLogo key={index} name={logo.name} />
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
